import {ColorSchemeType} from 'platform/components';

import {useState} from 'react';

import {
  RecordAuditResponseBody,
  RecordAuditState,
  useCloseRecordAuditMutation,
  useLazyExportRecordAuditPdfQuery,
  useReopenRecordAuditMutation,
} from '@dms/api/core';

import {ApiError} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';

export const RECORD_AUDIT_STATE_COLOR_SCHEME: Record<RecordAuditState, ColorSchemeType> = {
  OPEN: 'blue',
  CLOSED: 'green',
  SIGNED: 'purple',
};

/**
 * Lifecycle actions shared by every record-audit surface (detail page, embedded fill manager):
 * close / reopen / export handlers with their loading flags, plus the sign-modal open state.
 * Consumers only map these onto their own action shape (EntityHeaderAction vs Button).
 */
export function useRecordAuditLifecycle(audit: RecordAuditResponseBody) {
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);

  const [closeAudit, closeState] = useCloseRecordAuditMutation();
  const [reopenAudit, reopenState] = useReopenRecordAuditMutation();
  const [exportPdf, exportPdfState] = useLazyExportRecordAuditPdfQuery();

  // No success toast on close/reopen: the state flag + actions already reflect the new state.
  const handleClose = () =>
    closeAudit({auditId: audit.id})
      .unwrap()
      .catch((error: ApiError) => handleApiError(error));

  const handleReopen = () =>
    reopenAudit({auditId: audit.id})
      .unwrap()
      .catch((error: ApiError) => handleApiError(error));

  // Open an export (PDF) blob in a new tab. The opened tab holds the blob URL while it
  // loads, so revoke after a tick rather than immediately.
  const openExport = (trigger: (args: {auditId: string}) => {unwrap: () => Promise<Blob>}) =>
    trigger({auditId: audit.id})
      .unwrap()
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      })
      .catch((error: ApiError) => handleApiError(error));

  return {
    handleClose,
    handleReopen,
    handleExportPdf: () => openExport(exportPdf),
    isClosing: closeState.isLoading,
    isReopening: reopenState.isLoading,
    isExportingPdf: exportPdfState.isFetching,
    isSignModalOpen,
    openSignModal: () => setIsSignModalOpen(true),
    closeSignModal: () => setIsSignModalOpen(false),
  };
}
